import React, {JSX, useEffect, useRef, useState} from "react";
import {AppBar, Toolbar, Container, Box, IconButton, Button, Typography, Paper, TextField, ClickAwayListener} from "@mui/material";
import { Link } from "react-router-dom";
//@ts-ignore
import oldLogo from "../../../assets/images/logo/vld-2026.png";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBars, faSearch, faXmark} from "@fortawesome/free-solid-svg-icons";
import { getCampaignStatus } from "../../../services/campaign.service";
import SearchPopover from "@navbar/search-popover.navbar";
import { useMatomo } from '@jonkoops/matomo-tracker-react';

const Desktop = () => {
    const { trackEvent } = useMatomo();
    const [isOpen, setIsOpen] = useState(false);
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [ignoreMouseEnter, setIgnoreMouseEnter] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [campaignActive, setCampaignActive] = useState(false);

    // Récupération du status de la campagne au montage
    useEffect(() => {
        const fetchCampaignStatus = async () => {
            try {
                const status = await getCampaignStatus();
                // @ts-ignore
                setCampaignActive(status.isActive === 1);
            } catch (error) {
                console.error("Erreur lors de la récupération du status de campagne :", error);
            }
        };

        fetchCampaignStatus();
    }, []);

    useEffect(() => {
        console.log(campaignActive);
    }, [campaignActive]);

    const handleSelectNavigation = () => {
        setIsOpen(false);
        setIsVisible(false);
    };

    // Tracking des clics sur le logo
    const handleLogoClick = () => {
        trackEvent({
            category: 'Navigation',
            action: 'Click',
            name: 'Logo Home',
        });
        handleSelectNavigation();
    };

    // Tracking de l'ouverture/fermeture du menu
    const toggleMenu = () => {
        const newState = !isOpen;
        setIsOpen(newState);

        trackEvent({
            category: 'Navigation',
            action: newState ? 'Open' : 'Close',
            name: 'Menu Hamburger',
        });

        setIgnoreMouseEnter(true);
        setTimeout(() => setIgnoreMouseEnter(false), 300);
    };

    // Tracking du hover sur les sections
    const handleMouseEnter = (section: string) => {
        if (ignoreMouseEnter) return;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        trackEvent({
            category: 'Navigation',
            action: 'Hover',
            name: `Section ${section}`,
        });

        setActiveSection(section);
        setIsVisible(true);
    };

    // Tracking des clics sur les boutons de navigation
    const handleNavigationClick = (destination: string, label: string) => {
        trackEvent({
            category: 'Navigation',
            action: 'Click',
            name: label,
        });
        handleSelectNavigation();
    };

    const sectionContent: Record<string, JSX.Element> = {
        "ACTUALITÉS": (
            <>
                <Typography variant="h6" gutterBottom>
                    Actualités
                </Typography>
                <Typography variant="body2">
                    Découvrez nos dernières actualités et suivez nos projets en cours.
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Button
                        component={Link}
                        to="/les-actualites"
                        variant="contained"
                        onClick={() => handleNavigationClick('/les-actualites', 'Bouton Actualités')}
                    >
                        Voir les dernières actualités
                    </Button>
                </Box>
            </>
        ),
        "PLUi": (
            <>
                <Typography variant="h6" gutterBottom>
                    PLUi
                </Typography>
                <Typography variant="body2">
                    Découvrez notre carte intéractive du nouveau plan local d'urbanisme intercommunal.
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Button
                        component={Link}
                        to="/carte-interactive"
                        variant="contained"
                        onClick={() => handleNavigationClick('/carte-interactive', 'Bouton Carte Interactive')}
                    >
                        Voir la carte
                    </Button>
                </Box>
            </>
        ),
        "ENQUÊTES": (
            <>
                <Typography variant="h6" gutterBottom>
                    Enquêtes
                </Typography>
                <Typography variant="body2">
                    Explorez nos enquêtes approfondies sur des sujets variés et essentiels.
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Button
                        component={Link}
                        to="/les-enquetes"
                        variant="contained"
                        onClick={() => handleNavigationClick('/les-enquetes', 'Bouton Enquêtes')}
                    >
                        Voir les enquêtes
                    </Button>
                </Box>
            </>
        ),
        "PUBLICATIONS": (
            <>
                <Typography variant="h6" gutterBottom>
                    Publications
                </Typography>
                <Typography variant="body2">
                    Accédez à nos rapports, études et autres documents officiels.
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Button
                        component={Link}
                        to="/les-publications"
                        variant="contained"
                        onClick={() => handleNavigationClick('/les-publications', 'Bouton Publications')}
                    >
                        Voir les publications
                    </Button>
                </Box>
            </>
        ),
        "F.A.Q": (
            <>
                <Typography variant="h6" gutterBottom>
                    Foire Aux Questions
                </Typography>
                <Typography variant="body2" gutterBottom>
                    Retrouvez les réponses aux questions les plus fréquentes concernant nos activités.
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Button
                        component={Link}
                        to="/faq"
                        variant="contained"
                        onClick={() => handleNavigationClick('/faq', 'Bouton FAQ')}
                    >
                        Voir la F.A.Q
                    </Button>
                </Box>
            </>
        ),
        "CONTACTEZ-NOUS": (
            <>
                <Typography variant="h6" gutterBottom>
                    Contactez-nous
                </Typography>
                <Typography variant="body2">
                    Vous avez une question ou une demande ? N'hésitez pas à nous écrire ou à nous appeler.
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <Button
                        component={Link}
                        to="/contactez-nous"
                        variant="contained"
                        onClick={() => handleNavigationClick('/contactez-nous', 'Bouton Contact')}
                    >
                        Formulaire de contact
                    </Button>
                </Box>
            </>
        ),
    };

    if (campaignActive) {
        sectionContent["MUNICIPALES 2026"] = (
            <>
                <Typography variant="h6" gutterBottom>
                    VLD 2026
                </Typography>
                <Typography variant="body2">
                    Informations sur les élections municipales de 2026.
                </Typography>
                <Box sx={{ mt: 2, display: "flex" }}>
                    <Button
                        component={Link}
                        to="/vld-2026/les-publications"
                        variant="contained"
                        onClick={() => handleNavigationClick('/vld-2026/les-publications', 'Bouton Tracts 2026')}
                    >
                        Tracts 2026
                    </Button>
                    <Button
                        component={Link}
                        to="/vld-2026/faire-un-don"
                        variant="contained"
                        color="success"
                        onClick={() => handleNavigationClick('/vld-2026/faire-un-don', 'Bouton Soutenir Campagne')}
                        sx={{ marginLeft: 1 }}
                    >
                        Soutenir la campagne
                    </Button>
                    <Button
                        component={Link}
                        to="/vld-2026/programme"
                        variant="contained"
                        color="primary"
                        onClick={() => handleNavigationClick('/vld-2026/programme', 'Bouton Programme 2026')}
                        sx={{ marginLeft: 1 }}
                    >
                        Programme 2026
                    </Button>
                    <Button
                        component={Link}
                        to="/vld-2026"
                        variant="contained"
                        color="warning"
                        onClick={() => handleNavigationClick('/vld-2026', 'Bouton En Savoir Plus VLD 2026')}
                        sx={{ marginLeft: 1 }}
                    >
                        En savoir plus
                    </Button>
                </Box>
            </>
        );
    }

    const getSectionOrder = () => {
        if (campaignActive) {
            return ["ACTUALITÉS", "PLUi", "MUNICIPALES 2026", "ENQUÊTES", "PUBLICATIONS", "F.A.Q", "CONTACTEZ-NOUS"];
        }
        return ["ACTUALITÉS", "PLUi", "ENQUÊTES", "PUBLICATIONS", "F.A.Q", "CONTACTEZ-NOUS"];
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsVisible(false);
            setActiveSection(null);
        }, 200);
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // SEARCH STATE AND HANDLERS
    const [showSearch, setShowSearch] = useState(false);
    const [searchAnchor, setSearchAnchor] = useState<null | HTMLElement>(null);
    const [query, setQuery] = useState("");
    const searchBoxRef = useRef<HTMLDivElement>(null);

    // Tracking de l'ouverture de la recherche
    const handleOpenSearch = (e: React.MouseEvent) => {
        trackEvent({
            category: 'Search',
            action: 'Open',
            name: 'Barre de recherche',
        });

        setShowSearch(true);
        setSearchAnchor(e.currentTarget.parentElement as HTMLElement);
        setTimeout(() => {
            const el = document.getElementById("navbar-search-input");
            if (el) (el as HTMLInputElement).focus();
        }, 0);
    };

    // Tracking de la recherche
    const handleSearch = (searchQuery: string) => {
        if (searchQuery.trim().length > 0) {
            trackEvent({
                category: 'Search',
                action: 'Query',
                name: searchQuery,
            });
        }
    };

    const handleCloseSearch = () => {
        if (query.trim().length > 0) {
            trackEvent({
                category: 'Search',
                action: 'Close',
                name: 'Sans résultat sélectionné',
            });
        }
        setShowSearch(false);
        setSearchAnchor(null);
        setQuery("");
    };

    // Debounce pour éviter de tracker chaque lettre
    useEffect(() => {
        const timer = setTimeout(() => {
            if (query.length >= 3) {
                handleSearch(query);
            }
        }, 1000); // Attendre 1 seconde après la dernière frappe

        return () => clearTimeout(timer);
    }, [query]);

    // -- RENDER --
    return (
        <>
            {/* Navbar principale */}
            <AppBar position="fixed" elevation={isOpen ? 4 : 1} sx={{ backgroundColor: "white" }}>
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Link to={"/"} onClick={handleLogoClick}>
                                <Box component="img" src={oldLogo} alt="Logo" sx={{ width: 230 }} />
                            </Link>
                            <IconButton onClick={toggleMenu} edge="start" sx={{ ml: 2, color: "black" }}>
                                {isOpen ? <FontAwesomeIcon icon={faXmark} /> : <FontAwesomeIcon icon={faBars} />}
                            </IconButton>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            {!showSearch && (
                                <IconButton
                                    sx={{ color: "black" }}
                                    onClick={handleOpenSearch}
                                >
                                    <FontAwesomeIcon icon={faSearch} />
                                </IconButton>
                            )}
                            {showSearch && (
                                <ClickAwayListener
                                    onClickAway={event => {
                                        const popover = document.getElementById("navbar-search-popover");
                                        if (
                                            (searchBoxRef.current && searchBoxRef.current.contains(event.target as Node)) ||
                                            (popover && popover.contains(event.target as Node))
                                        ) {
                                            return;
                                        }
                                        handleCloseSearch();
                                    }}
                                >
                                    <Box ref={searchBoxRef} sx={{ display: "flex", alignItems: "center", ml: 2 }}>
                                        <Paper
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                p: 0.5,
                                                pr: 0.75,
                                                minWidth: 240,
                                                borderRadius: 2,
                                                boxShadow: 2,
                                                position: "relative",
                                                zIndex: 1300,
                                            }}
                                        >
                                            <TextField
                                                id="navbar-search-input"
                                                variant="standard"
                                                placeholder="Rechercher..."
                                                value={query}
                                                onChange={e => setQuery(e.target.value)}
                                                autoFocus
                                                sx={{ minWidth: 180, ml: 1 }}
                                                InputProps={{
                                                    disableUnderline: true,
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Escape") handleCloseSearch();
                                                }}
                                            />
                                            <IconButton
                                                aria-label="Fermer la recherche"
                                                onClick={handleCloseSearch}
                                                size="small"
                                                sx={{ color: "grey.600", ml: 1 }}
                                            >
                                                <FontAwesomeIcon icon={faXmark} />
                                            </IconButton>
                                        </Paper>
                                        <SearchPopover
                                            anchorEl={searchAnchor}
                                            open={showSearch}
                                            handleClose={handleCloseSearch}
                                            query={query}
                                            popoverId="navbar-search-popover"
                                        />
                                    </Box>
                                </ClickAwayListener>
                            )}
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>
            {/* Menu déroulant */}
            {isOpen && (
                <div style={{ position: "fixed", left: 0, top: '80px', width: '100vw', zIndex: 1101, backgroundColor: "white", boxShadow: "0px 2px 3px rgba(0, 0, 0, 0.2)" }} onMouseLeave={handleMouseLeave}>
                    <Container maxWidth="lg">
                        <Box sx={{ display: "flex", justifyContent: "space-around", py: 2 }}>
                            {getSectionOrder().map((section) => (
                                <Box
                                    key={section}
                                    onMouseEnter={() => handleMouseEnter(section)}
                                    sx={{ cursor: "pointer" }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            textDecoration: activeSection === section && isVisible ? "underline" : "none",
                                            textDecorationColor: "orange",
                                            textDecorationThickness: "2px",
                                            textUnderlineOffset: "8px",
                                        }}
                                    >
                                        {section}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Container>
                </div>
            )}

            {/* Affichage du contenu de la section active */}
            {isVisible && activeSection && (
                <div style={{ position: "fixed", left: 0, top: '130px', width: '100vw', backgroundColor: 'white', zIndex: 1102, boxShadow: "0px 2px 3px rgba(0, 0, 0, 0.2)"}} onMouseEnter={() => {
                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    setIsVisible(true);
                }}
                     onMouseLeave={handleMouseLeave}
                >
                    <Container maxWidth="lg" sx={{ p: 2 }}>{sectionContent[activeSection]}</Container>
                </div>
            )}

        </>
    );
};

export default Desktop;
